import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Heart, Repeat, Package, HandHeart, HandCoins, Users, ArrowRight, Lock, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { DonationModal } from "../components/DonationModal";
import { DonationProgress } from "../components/DonationProgress";
import { StorySpotlight } from "../components/StorySpotlight";
import { SupporterWall } from "../components/SupporterWall";
import { SITE } from "../data/content";

const funds = [
  { amount: 50, label: "$50", body: "Stocks a welcome kit — bedding, toiletries, and essentials for a veteran's first night." },
  { amount: 250, label: "$250", body: "Covers a week of structured transitional housing and daily support for one veteran." },
  { amount: 1000, label: "$1,000", body: "Funds a full stabilization cycle: housing, case management, and benefits navigation." },
];

const ways = [
  { icon: HandCoins, title: "Donate", body: "One-time or recurring gifts fund housing, case management, and reset support — the core of what we do." },
  { icon: Users, title: "Volunteer", body: "Share professional skills, mentor a veteran, or help run programs and community events." },
  { icon: HandHeart, title: "Partner", body: "Bring your organization's resources, referrals, or funding to expand our reach together." },
];

const Donate = () => {
  const [params] = useSearchParams();
  const [modal, setModal] = useState({ open: false, frequency: "one_time", amount: null });
  useEffect(() => { document.title = "Support Iron Shield Veterans Reset Center"; }, []);
  useEffect(() => {
    if (params.get("donation") === "cancelled") {
      toast.info("Donation cancelled — no charge was made. You can give any time.");
    }
  }, [params]);

  const openModal = (frequency = "one_time", amount = null) => setModal({ open: true, frequency, amount });

  return (
    <>
      <section className="bg-navy text-offwhite pt-[130px] md:pt-[150px] pb-14 md:pb-16 border-b-4 border-gold" data-testid="donate-hero">
        <div className="container-x max-w-4xl">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-white/10 text-gold text-xs font-bold uppercase tracking-widest">
            <Lock className="w-3.5 h-3.5" /> Public Mission Support
          </div>
          <h1 className="mt-4 font-heading text-4xl md:text-6xl font-black tracking-tight">Support stability for veterans in crisis.</h1>
          <p className="mt-5 text-offwhite/80 md:text-lg leading-relaxed">
            Your support helps Iron Shield build the structure, systems, and support pathways needed to serve veterans facing housing instability — with clear communication, responsible stewardship, and transparent growth.
          </p>
        </div>
      </section>

      <DonationProgress onGive={() => openModal("one_time")} />

      <StorySpotlight onGive={() => openModal("one_time")} />

      {/* Ways to help */}
      <section className="py-20 md:py-28 bg-offwhite" data-testid="donate-ways">
        <div className="container-x">
          <SectionHeading eyebrow="Ways to help" title="Three ways to stand with veterans." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {ways.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.1} className="i-card p-8 h-full">
                <w.icon className="h-6 w-6 text-navy" strokeWidth={1.75} />
                <h3 className="mt-5 font-heading text-2xl font-bold text-navy">{w.title}</h3>
                <p className="mt-3 text-charcoal/75 leading-relaxed">{w.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* What support funds */}
      <section className="py-20 md:py-28 bg-white border-y border-softgray" data-testid="donate-funds">
        <div className="container-x">
          <SectionHeading eyebrow="What support funds" title="Every dollar has a job." intro="We keep the connection between your gift and its impact direct and visible." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-softgray border border-softgray">
            {funds.map((f, i) => (
              <Reveal key={f.label} delay={i * 0.1} className="bg-white">
                <button onClick={() => openModal("one_time", f.amount)} data-testid={`donate-fund-${f.amount}`} className="group p-8 h-full w-full text-left hover:bg-offwhite transition-colors">
                  <div className="font-heading text-4xl font-black text-navy">{f.label}</div>
                  <div className="mt-2 h-0.5 w-10 bg-gold" />
                  <p className="mt-5 text-charcoal/75 leading-relaxed">{f.body}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 label-caps text-navy group-hover:text-gold-deep transition-colors">
                    Give ${f.amount} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Donation CTA modules */}
      <section className="py-20 md:py-28 bg-navy text-offwhite" data-testid="donate-cta">
        <div className="container-x grid grid-cols-1 md:grid-cols-2 gap-6">
          <Reveal className="i-card-dark p-10">
            <Heart className="h-7 w-7 text-gold" />
            <h3 className="mt-5 font-heading text-2xl md:text-3xl font-black">One-time gift</h3>
            <p className="mt-3 text-offwhite/75 leading-relaxed">Make an immediate impact. Give once, give what you can — it all goes to work right away.</p>
            <button onClick={() => openModal("one_time")} data-testid="donate-onetime-btn" className="group mt-7 inline-flex items-center gap-2 bg-offwhite text-navy px-7 py-4 rounded-sm text-sm font-semibold hover:bg-white transition-colors">
              Give once <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </Reveal>
          <Reveal delay={0.1} className="i-card-dark p-10 relative border-gold/50">
            <span className="absolute top-6 right-6 label-caps text-gold">Most impact</span>
            <Repeat className="h-7 w-7 text-gold" />
            <h3 className="mt-5 font-heading text-2xl md:text-3xl font-black">Monthly support</h3>
            <p className="mt-3 text-offwhite/75 leading-relaxed">Recurring gifts give us the stability to plan ahead and keep beds open for veterans in crisis.</p>
            <button onClick={() => openModal("monthly")} data-testid="donate-monthly-btn" className="group mt-7 inline-flex items-center gap-2 bg-gold text-charcoal px-7 py-4 rounded-sm text-sm font-semibold hover:bg-gold-deep transition-colors">
              Give monthly <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </Reveal>
        </div>
      </section>

      {/* In-kind + trust */}
      <section className="py-20 md:py-28 bg-offwhite" data-testid="donate-inkind">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeading eyebrow="In-kind support" title="Not just money helps." />
            <Reveal delay={0.1}>
              <p className="mt-6 text-charcoal/80 md:text-lg leading-relaxed">
                Furniture, bedding, hygiene supplies, gift cards, and professional services all make a real difference. If you or your organization can contribute in-kind, we'd love to hear from you.
              </p>
              <Link to="/contact" className="group mt-8 inline-flex items-center gap-2 label-caps text-navy hover:text-gold-deep transition-colors" data-testid="donate-inkind-contact">
                Arrange an in-kind gift <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="i-card p-8">
              <Package className="h-6 w-6 text-navy" strokeWidth={1.75} />
              <div className="mt-5 label-caps text-navy">Transparency</div>
              <p className="mt-3 text-charcoal/75 leading-relaxed text-sm">
                Iron Shield is committed to full financial transparency. As our 501(c)(3) status is finalized, we'll publish annual impact reports, outcomes, and independent financials here.
              </p>
              <a href={`mailto:${SITE.email}`} className="mt-4 inline-block font-semibold text-navy hover:text-gold-deep transition-colors" data-testid="donate-transparency-email">{SITE.email}</a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white border-t border-softgray" data-testid="donate-trust">
        <div className="container-x max-w-4xl space-y-6">
          <div className="i-card p-8 space-y-3">
            <div className="flex items-center gap-2.5 text-navy">
              <ShieldCheck className="w-5 h-5 text-gold" />
              <h2 className="text-xl font-bold uppercase tracking-wide">Why give to Iron Shield now?</h2>
            </div>
            <p className="text-sm md:text-base text-charcoal leading-relaxed">
              Iron Shield is being built to meet a real need with a focused, veteran-centered model grounded in safety, structure, and dignity. Early support helps create the foundation for a transparent, accountable organization designed to grow responsibly.
            </p>
          </div>

          <div className="i-card p-8 space-y-6">
            <div className="flex items-center justify-between border-b border-softgray pb-4">
              <div className="flex items-center gap-2.5 text-navy">
                <Lock className="w-5 h-5 text-gold" />
                <h2 className="text-xl font-bold uppercase tracking-wide">Our commitment to donors</h2>
              </div>
              <span className="text-xs font-bold text-navy bg-navy/10 px-3 py-1 rounded-full uppercase tracking-wider">Donor Charter</span>
            </div>
            <ul className="space-y-3.5 text-sm text-charcoal">
              {[
                "We will communicate clearly about what Iron Shield is building.",
                "We will share organizational and financial updates as they become available.",
                "We will publish accountability information and milestone progress on this website.",
                "We will avoid overstating our status, impact, or readiness.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 bg-offwhite p-4 rounded-lg border border-softgray">
                  <CheckCircle2 className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <div className="pt-1 text-center">
              <Link to="/accountability" data-testid="donate-accountability-link" className="group inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-navy hover:text-navy-deep border-b-2 border-navy pb-0.5 transition-colors">
                Read Full Accountability & Transparency Statement <ArrowRight className="w-3.5 h-3.5 text-gold transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <SupporterWall />

      <DonationModal
        open={modal.open}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        defaultFrequency={modal.frequency}
        defaultAmount={modal.amount}
      />
    </>
  );
};

export default Donate;
